import moment from "moment";
import { useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PaymentWiseReportTableData = ({ data, summary, startDate, endDate, onReady }) => {

    // ---------------- Helpers ----------------
    const number_format = (number, decimals = 2) => {
        return Number(number || 0).toLocaleString("en-IN", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    };

    const allPaymentTypes = [
        "Cash",
        "Axis Bank Pos",
        "Phonepe - UCO Bank Pos",
        "Gpay UPI",
        "Phonepe upi",
        "Paytm upi",
        "Bharat pay upi",
        "Debit card",
        "Credit card",
        "Bhim upi",
        "Amazon pay upi",
        "Card",
        "UPI",
        "Partial Payment"
    ];

    const paymentTypes = useMemo(() => {
        return allPaymentTypes; // always show all types
    }, []);

    // Count bills by payment type
    const paymentTypeCounts = useMemo(() => {
        const counts = {};
        paymentTypes.forEach(type => {
            counts[type] = data[type]?.length || 0;
        });
        return counts;
    }, [paymentTypes, data]);

    // Flatten all transactions with payment type
    const allTransactions = useMemo(() => {
        if (!data) return [];
        const transactions = [];

        Object.entries(data).forEach(([paymentType, bills]) => {
            if (bills && Array.isArray(bills)) {
                bills.forEach(bill => {
                    transactions.push({
                        ...bill,
                        paymentType,
                        date: moment(bill.date).format("YYYY-MM-DD")
                    });
                });
            }
        });

        // Sort by date descending
        return transactions.sort((a, b) => moment(b.date) - moment(a.date));
    }, [data]);

    // Calculate totals by payment type
    const paymentTypeTotals = useMemo(() => {
        const totals = {};
        paymentTypes.forEach(type => {
            totals[type] = data[type]?.reduce((sum, bill) => sum + Number(bill.payment || 0), 0) || 0;
        });
        return totals;
    }, [paymentTypes, data]);

    const grandTotal = useMemo(() => {
        return Object.values(paymentTypeTotals).reduce((sum, val) => sum + val, 0);
    }, [paymentTypeTotals]);

    // Export to Excel
    const handleExportToExcel = () => {
        // Build headers: Date, Bill No, Order No, then payment types
        const headers = ["Date", ...paymentTypes, "Total"];

        // Build rows data
        const exportRows = allTransactions.map(transaction => {
            const row = [moment(transaction.date).format("DD-MMM-YYYY")];
            let rowTotal = 0;

            paymentTypes.forEach(type => {
                const val = transaction.paymentType === type ? Number(transaction.payment) : 0;
                row.push(val);
                rowTotal += val;
            });

            row.push(rowTotal); // add row total
            return row;
        });

        // Add total row
        const totalRow = [
            "TOTAL",
            ...paymentTypes.map(type => paymentTypeTotals[type]),
            Object.values(paymentTypeTotals).reduce((sum, val) => sum + val, 0), // grand total
        ];


        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet([headers, ...exportRows, totalRow]);

        // Set column widths
        const colWidths = [15, 18, 18, ...paymentTypes.map(() => 15)];
        ws["!cols"] = colWidths.map(width => ({ wch: width }));

        // Create workbook and add worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payment Wise Report");

        // Save file
        const fileName = `Payment_Wise_Sales_${moment(startDate).format("DD-MMM-YYYY")}_to_${moment(endDate).format("DD-MMM-YYYY")}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // useEffect(() => {
    //     if (onReady) {
    //         onReady({ allTransactions, paymentTypes, grandTotal });
    //     }
    // }, [allTransactions, paymentTypes, grandTotal]); // run ONLY when data changes


    useEffect(() => {
        if (onReady) {
            onReady({
                exportExcel: handleExportToExcel
            });
        }
    }, [allTransactions]);



    const statsConfig = [
        {
            label: "Total Bills Counts",
            value: allTransactions?.length || 0,
            bg: "#FFF7ED",
            color: "#4338CA"
        },
        {
            label: "Total Amount",
            value: number_format(grandTotal),
            bg: "#FEF2F2",
            color: "#15803D"
        }
    ];

    
    const StatsCards = () => (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "20px",
                marginBottom: "15px",
                marginTop: "20px"
            }}
        >
            {statsConfig.map((item, index) => (
            <div
                key={index}
                style={{
                ...cardStyle,
                backgroundColor: item.bg,
                borderLeft: `5px solid ${item.color}`
                }}
            >
                <div style={{ ...labelStyle, color: item.color }}>
                {item.label}
                </div>
                <div style={{ ...valueStyle, color: item.color }}>
                {item.value}
                </div>
            </div>
            ))}
        </div>
    );


    const cardStyle = {
        padding: "15px",
        background: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
    };

    const labelStyle = {
        fontSize: "14px",
        color: "#6b7280",
        fontWeight: "500",
    };

    const valueStyle = {
        fontSize: "20px",
        fontWeight: "700",
        color: "#111827",
        marginTop: "5px",
    };
    // ---------------- UI ----------------
    return (
        <div style={{ overflowX: "auto", marginTop: "20px" }}>

            <StatsCards />
            <table style={tableStyle}>
                
                <thead>
                    <tr>
                        <th style={thStyle}>Date</th>
                        {paymentTypes.map(type => (
                            <th key={type} style={thStyle}>
                                {type} ({paymentTypeCounts[type]})
                            </th>
                        ))}
                        <th style={thStyle}>Total</th> {/* <-- New header */}
                    </tr>
                </thead>
                <thead>
                    <tr>
                        <td style={tdBold}>TOTAL</td>
                        {paymentTypes.map(type => (
                            <td key={type} style={tdBoldRight}>{number_format(paymentTypeTotals[type])}</td>
                        ))}
                        <td style={tdBoldRight}>{number_format(grandTotal)}</td> {/* <-- Grand total */}
                    </tr>
                </thead>

                <tbody>
                    {allTransactions.map((transaction, idx) => {
                        let rowTotal = 0;
                        return (
                            <tr key={idx}>
                                <td style={tdBold}>{moment(transaction.date).format("DD-MMM-YYYY")}</td>
                                {paymentTypes.map(type => {
                                    const val = transaction.paymentType === type ? Number(transaction.payment) : 0;
                                    rowTotal += val;
                                    return <td key={type} style={tdStyle}>{number_format(val)}</td>;
                                })}
                                <td style={tdBoldRight}>{number_format(rowTotal)}</td> {/* <-- Row total */}
                            </tr>
                        );
                    })}
                </tbody>

                <tfoot>
                    <tr>
                        <td style={tdBold}>TOTAL</td>
                        {paymentTypes.map(type => (
                            <td key={type} style={tdBoldRight}>{number_format(paymentTypeTotals[type])}</td>
                        ))}
                        <td style={tdBoldRight}>{number_format(grandTotal)}</td> {/* <-- Grand total */}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default PaymentWiseReportTableData;


const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    whiteSpace: "nowrap",
    fontSize: "14px",
};

const thStyle = {
    border: "1px solid #e5e7eb",
    padding: "10px",
    background: "#f9fafb",
    fontWeight: 600,
    textAlign: "center",
};

const tdStyle = {
    border: "1px solid #e5e7eb",
    padding: "8px",
    textAlign: "right",
};

const tdBold = {
    ...tdStyle,
    fontWeight: 600,
    textAlign: "left",
    background: "#fcfcfc",
};

const tdBoldRight = {
    ...tdStyle,
    fontWeight: 700,
    background: "#f3f4f6",
};